import { Capacitor } from '@capacitor/core';
import { Build, Component, ComponentInterface, h, Host, State } from '@stencil/core';
import { store, Unsubscribe } from '@stencil/redux';
import AppDatabase from '../../services/db/app-db.service';

import { loadApp } from '../../store/app/app.actions';
import { AppStatus } from '../../store/app/app.reducer';
import { ApplicationState } from '../../store/reducers';
import { getAppFeatureIsEnabled, getAppId, getAppIsDev, getAppName, getAppPagesForMenu, getAuthUser, getAuthUserImageUrl, getAvailableApps } from '../../store/selectors';
import { CoreoAppPage, CoreoRecordSyncStatus, CoreoUser } from '../../types';

@Component({
  tag: 'app-menu',
  styleUrl: 'menu.scss',
  scoped: true
})
export class CoreoMenu implements ComponentInterface {

  @State() userImageUrl: string;
  @State() apps: AppStatus[];
  @State() appId: number;
  @State() appName: string;
  @State() user: CoreoUser;
  @State() pages: CoreoAppPage[];
  @State() pending: { [key: number]: number };
  @State() offlineMapsEnabled: boolean;

  loadApp!: typeof loadApp;

  @State() isDev = false;
  private unsubscribe: Unsubscribe;
  private isNative: boolean = true;

  private updatePending = async () => {
    const pending = await AppDatabase.instance.records.query({ syncState: CoreoRecordSyncStatus.PENDING_UPDATE })

    const pendingMap = Object.fromEntries(this.apps.map(app => [app.id, 0]));
    for (const p of pending) {
      pendingMap[p.projectId]++;
    };
    this.pending = pendingMap;
  };

  componentWillLoad() {
    this.unsubscribe = store.mapStateToProps(this, (state: ApplicationState) => ({
      userImageUrl: getAuthUserImageUrl(state),
      apps: getAvailableApps(state),
      appId: getAppId(state),
      appName: getAppName(state),
      user: getAuthUser(state),
      pages: getAppPagesForMenu(state),
      isDev: getAppIsDev(state),
      offlineMapsEnabled: getAppFeatureIsEnabled('offlineMaps')(state)
    }));

    store.mapDispatchToProps(this, {
      loadApp
    });

    AppDatabase.instance.events.addListener('syncDidUpdate', this.updatePending);

    this.isNative = Capacitor.isNativePlatform();
    this.updatePending();
  }


  disconnectedCallback() {
    AppDatabase.instance.events.removeListener('syncDidUpdate', this.updatePending);
    this.unsubscribe();
  }

  switchApp(app) {
    // Don't reload the same app
    if (this.appId === app.id) {
      return;
    }

    this.loadApp(app.id);
    document.querySelector('ion-nav').popToRoot();
  }

  renderWorkspaceSwitcher() {
    return [
      this.apps.map(app =>
        <ion-menu-toggle autoHide={false}>
          <ion-avatar class={{ 'menu__app': true, active: app.id === this.appId }} onClick={() => this.switchApp(app)}>
            <coreo-app-icon appId={app.id} />
            {this.pending?.[app.id] > 0 && <ion-badge>{this.pending[app.id]}</ion-badge>}
          </ion-avatar>
        </ion-menu-toggle>
      ),
      <ion-menu-toggle autoHide={false}>
        <ion-router-link href="/projects">
          <div class="menu__app">
            <ion-icon name="add" />
          </div>
        </ion-router-link>
      </ion-menu-toggle>
    ];
  }

  renderPendingRecordsPage() {
    return [
      <ion-router-link href={`/pending`}>
        <ion-item>
          <ion-icon name="ellipsis-horizontal-circle-outline" size="small" slot="start"></ion-icon>
          <ion-label>Pending Updates</ion-label>
          <ion-badge slot="end" class="ion-no-margin">{this.pending?.[this.appId] ?? 0}</ion-badge>
        </ion-item>
      </ion-router-link>
    ]
  }

  renderPages() {
    return (
      <ion-menu-toggle autoHide={false}>
        <ion-list inset={false} lines="none">
          {this.appId && this.renderPendingRecordsPage()}
          {
            this.pages.map(page =>
              <ion-router-link href={`/pages/${page.id}`}>
                <ion-item>
                  <ion-icon name={page.icon} size="small" slot="start"></ion-icon>
                  <ion-label>{page.title}</ion-label>
                </ion-item>
              </ion-router-link>
            )
          }
        </ion-list>
      </ion-menu-toggle>
    );
  }

  renderFixed() {
    return (
      <ion-list lines="none">
        {this.isDev && <ion-item color="warning">
          <ion-icon slot="start" name="bug-outline" />
          <ion-label>Dev Environment</ion-label>
        </ion-item>}
        {(this.offlineMapsEnabled && (this.isNative || Build.isDev)) && <ion-menu-toggle autoHide={false}>
          <ion-router-link href="/maps">
            <ion-item detail={false} >
              <ion-icon slot="start" name="map-outline" />
              <ion-label>Offline Maps</ion-label>
            </ion-item>
          </ion-router-link>
        </ion-menu-toggle>}
        <ion-menu-toggle autoHide={false}>
          <ion-router-link href="/account">
            <ion-item detail={false} >
              <ion-icon slot="start" name="person-outline" />
              <ion-label>Account</ion-label>
            </ion-item>
          </ion-router-link>
        </ion-menu-toggle>
        <ion-menu-toggle autoHide={false}>
          <ion-router-link href="/projects">
            <ion-item detail={false} >
              <ion-icon slot="start" name="apps-outline" />
              <ion-label>Projects</ion-label>
            </ion-item>
          </ion-router-link>
        </ion-menu-toggle>
        <ion-menu-toggle autoHide={false}>
          <ion-router-link href="/settings">
            <ion-item detail={false} >
              <ion-icon slot="start" name="cog-outline" />
              <ion-label>Settings</ion-label>
            </ion-item>
          </ion-router-link>
        </ion-menu-toggle>
        <ion-menu-toggle autoHide={false}>
          <ion-router-link href="/help">
            <ion-item detail={false}>
              <ion-icon slot="start" name="help-circle-outline" />
              <ion-label>Help &amp; Support</ion-label>
            </ion-item>
          </ion-router-link>
        </ion-menu-toggle>
      </ion-list>
    );
  }

  render() {
    const imageUrl = this.userImageUrl ?? '/assets/imgs/avatar.svg';

    return (
      <Host>
        <div class="menu__apps">
          {this.renderWorkspaceSwitcher()}
        </div>
        <div class="menu__main">
          <div class="menu__header">
            <ion-avatar>
              <ion-img src={imageUrl} />
            </ion-avatar>
            <h2>{this.user?.displayName ?? ''}</h2>
            <p>{this.user?.username ?? ''}</p>
          </div>
          {this.appId && <div class="menu__current-app">
            <coreo-app-icon appId={this.appId} slot="start" />
            <ion-label>{this.appName}</ion-label>
          </div>}
          <div class="menu__pages">
            {this.renderPages()}
          </div>
          <div class="menu__coreo-nav">
            {this.renderFixed()}
          </div>
        </div>
      </Host>
    );
  }
}
